import { Col, Form, Input, Radio, Row, Select, Switch } from 'antd';
import Box from 'components/Box';
import InputDOB from 'components/InputDOB';
import InputEmail from 'components/InputEmail';
import Text from 'components/Text';
import colorTheme from 'constants/color';
import { IStaffPayload } from 'features/checkIn/services/types/demoData';
import settingStaffActions from 'features/settingStaff/services/actions';
import {
  STAFF_POSITION_INFO,
  positionsArray,
} from 'features/settingStaff/services/constants';
import settingStaffSelectors from 'features/settingStaff/services/selectors';
import { IStaffInfo } from 'features/settingStaff/services/types/staff';
import moment from 'moment';
import React, { useEffect } from 'react';
import { useAppDispatch } from 'store/hooks';
import styled from 'styled-components';
import { formatPhone } from 'utils/formatPhone';
import ButtonGroup from './ButtonGroup';
import PermissionManager from 'widgets/PermissionVerify';

type IStaffInformationProps = {
  v2?: boolean;
};

const StaffInformation: React.FC<IStaffInformationProps> = ({ v2 }) => {
  const selectedStaff = settingStaffSelectors.selectedStaffData.data();
  const [form] = Form.useForm<IStaffInfo>();
  const { Option } = Select;
  const dispatch = useAppDispatch();

  const onFinish = async (values: IStaffInfo) => {
    if (!(await form.validateFields())) {
      return;
    }

    const result = await PermissionManager.verify(PermissionManager.PERMISSIONS.STAFF_EDIT);
    if (result?.skip === false && !result.verifyData) return;

    const body: IStaffPayload = {
      staffId: selectedStaff?.id || '',
      firstName: values.firstName,
      lastName: values.lastName,
      nickName: values.nickName || '',
      phone: values.phone,
      email: values.email,
      autoClock: values.autoClock || false,
      staffType: values.staffType,
      // gender: values.gender,
      dob: values.birthday ? moment(values.birthday).format('MM-DD-YYYY') : '',
      street: values.address || '',
      city: values.city || '',
      state: values.state || '',
      zip: values.zip || '',
      status: values.status,
      socialSecurity: values.socialSecurity,
    };
    dispatch(settingStaffActions.createOrUpdateStaff.fetch(body));
  };

  const defaultValues: IStaffInfo = {
    avatar: '',
    id: '',
    autoClock: null,
    status: 'ACTIVE',
    firstName: '',
    lastName: '',
    nickName: '',
    phone: '',
    email: '',
    birthday: null,
    staffType: STAFF_POSITION_INFO.TECHNICAL,
    address: '',
    city: '',
    state: '',
    zip: '',
    socialSecurity: '',
  };

  useEffect(() => {
    if (selectedStaff) {
      form.setFieldsValue({
        avatar: selectedStaff.avatar,
        id: selectedStaff.id,
        status: selectedStaff.status,
        autoClock: !!selectedStaff.autoClock,
        firstName: selectedStaff.firstName,
        lastName: selectedStaff.lastName,
        nickName: selectedStaff.nickName || '',
        phone: formatPhone(selectedStaff.phone),
        email: selectedStaff.email,
        birthday: selectedStaff.birthday
          ? moment(selectedStaff.birthday).format('MM/DD/YYYY')
          : moment().format('MM/DD/YYYY'),
        staffType: selectedStaff.staffType,
        address: selectedStaff?.address || '',
        city: selectedStaff?.city || '',
        state: selectedStaff?.state || '',
        zip: selectedStaff?.zip || '',
        socialSecurity: selectedStaff?.socialSecurity
      });
    }
  }, [selectedStaff]);

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={onFinish}
      initialValues={defaultValues}
    >
      <StaffInformationStyled className={v2 ? 'v2' : ''}>
        <div className="gradient-box"></div>
        <Box>
          <Row gutter={24} style={v2 ? { margin: 0 } : {}}>
            <Col span={12}>
              <Row justify={'space-between'}>
                <Form.Item
                  name="status"
                  label="Status"
                  rules={[
                    { required: true, message: 'Please select an option!' },
                  ]}
                >
                  <Radio.Group>
                    <Radio value={'ACTIVE'}>Active</Radio>
                    <Radio value={'INACTIVE'}>Inactive</Radio>
                  </Radio.Group>
                </Form.Item>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: '8px' }}>
                  <Text variant='CONTENT_2'>Auto clock-in</Text>
                  <Form.Item noStyle name={'autoClock'} fieldId='checked' valuePropName='checked'>
                    <Switch checkedChildren="ON" unCheckedChildren="OFF" />
                  </Form.Item>
                </div>
              </Row>
              <Row justify={'space-between'} style={{ gap: 16 }}>
                <Form.Item
                  name="nickName"
                  label="Nick Name"
                  style={{ flex: 1 }}
                >
                  <Input placeholder="Enter your Nick Name" />
                </Form.Item>
                <Form.Item
                  name="firstName"
                  label="First Name"
                  style={{ flex: 1 }}
                >
                  <Input placeholder="Enter your First Name" />
                </Form.Item>
                <Form.Item
                  name="lastName"
                  label="Last Name"
                  style={{ flex: 1 }}
                  rules={[
                    { required: true, message: 'Please input your Last Name!' },
                  ]}
                >
                  <Input placeholder="Enter your Last Name" />
                </Form.Item>
              </Row>
              <Form.Item
                name="phone"
                label="Phone"
              >
                <Input
                  placeholder="Enter your phone number"
                  onChange={(e) => {
                    const formattedValue = formatPhone(e.target.value);
                    form.setFieldsValue({ phone: formattedValue });
                  }}
                />
              </Form.Item>
              <Row justify={'space-between'} style={{ gap: 2 }}>
                <Form.Item
                  name="socialSecurity"
                  label="Social Security #"
                  style={{ flex: 1 }}
                >
                  <Input placeholder="Enter your Social Security" />
                </Form.Item>
                <Form.Item
                  name="birthday"
                  label={'DOB'}
                  rules={[{ required: true, message: 'Please input birthday!' }]}
                  style={{ flex: 1 }}
                >
                  <InputDOB />
                </Form.Item>
              </Row>
              <InputEmail
                name={'email'}
                label="Email (Optional)"
                rules={[
                  { type: 'email' },
                ]}
              />
            </Col>
            <Col span={12}>
              <Form.Item
                name="staffType"
                label="Position"
                rules={[
                  { required: true, message: 'Please input your staff type!' },
                ]}
              >
                <Select id="staffInformation-staffType">
                  {positionsArray.map((item) => (
                    <Option key={item.value} value={item.value}>
                      {item.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <AddressStyled className='AddressStyled'>
                <AddressHeaderStyled className='AddressHeaderStyled'>
                  <Text variant="H9">Address</Text>
                </AddressHeaderStyled>
                <Box px="3" py="3">
                  <Form.Item name="address" label="Street">
                    <Input placeholder="Enter your Street" />
                  </Form.Item>

                  <Form.Item name="city" label="City">
                    <Input placeholder="Enter your City" />
                  </Form.Item>

                  <Form.Item name="state" label="State">
                    <Input placeholder="Enter your State" />
                  </Form.Item>

                  <Form.Item name="zip" label="Zip Code">
                    <Input placeholder="Enter your Zip Code" />
                  </Form.Item>
                </Box>
              </AddressStyled>
            </Col>
          </Row>
        </Box>
        <ButtonGroup />
      </StaffInformationStyled>
    </Form>
  );
};

export default StaffInformation;
const StaffInformationStyled = styled.div`
  border-radius: 5px;
  border: 1px solid ${colorTheme.line_3};
  background: #fff;
  box-shadow: -1px 1px 4px 0px rgba(0, 0, 0, 0.1) inset,
    0px 4px 4px 0px rgba(0, 0, 0, 0.25);
  padding: 1.5rem 4rem;

  .ant-radio-checked .ant-radio-inner {
    background: linear-gradient(to bottom, #548088, #6faab6);
    border-color: #6faab6;
  }

&.v2 {
  padding: 0px;
  border: none;
  box-shadow: none;
  background: transparent;

  .AddressStyled {
    background: #F6F7FC;
    box-shadow: none;
    .AddressHeaderStyled {
      background: #E5E6EB;
    }
  }
}
`;
const AddressHeaderStyled = styled.div`
  display: flex;
  align-items: center;
  margin-top: 1.5rem;
  height: 3rem;
  padding: 0 1rem;
  background-color: #adadad;
`;
const AddressStyled = styled.div`
  margin-top: 1.5rem;
  background: #f6f7fc;

  box-shadow: -1px 1px 4px 0px rgba(0, 0, 0, 0.1) inset,
    0px 4px 4px 0px rgba(0, 0, 0, 0.25);
`;
